import React from "react";
import { FileText, Eye, Trash2 } from "lucide-react";
import { ConfirmButton } from "../ui/primitives.jsx";
import { UploadBtn } from "../ui/attachments.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { fmtDate } from "../lib/format.js";
import { useAgreements, useAddAgreement, useDeleteAgreement } from "./agreements-api.js";

export default function Agreements({ customerId, onView }) {
  const { notify } = useToast();
  const list = useAgreements(customerId);
  const add = useAddAgreement(customerId);
  const del = useDeleteAgreement(customerId);

  // Uploading needs a customer row to attach to, so on a brand new customer the
  // control says why instead of failing after the file has already gone up.
  if (!customerId) {
    return (
      <div style={{ fontSize: 13, color: "var(--muted)" }}>
        Save the customer first, then reopen to attach signed agreements.
      </div>
    );
  }

  return (
    <>
      {list.error && <div className="errnote">{list.error.message}</div>}

      {(list.data ?? []).map((a) => (
        <div key={a.id} className="attrow">
          <FileText size={16} color="var(--muted)" />
          <span style={{ flex: 1 }}>
            {a.file_name || "Agreement"}{" "}
            <span className="sub-cell">· uploaded {fmtDate(a.uploaded)}</span>
          </span>
          <button type="button" className="iconbtn" title="View" onClick={() => onView({ path: a.file_path, name: a.file_name })}>
            <Eye size={16} />
          </button>
          <ConfirmButton
            className="iconbtn"
            title="Remove"
            style={{ color: "var(--red)", borderRadius: 5, padding: "5px 6px" }}
            label={<Trash2 size={15} />}
            confirmLabel="Remove?"
            disabled={del.isPending}
            onConfirm={async () => {
              try { await del.mutateAsync(a); notify("Agreement removed."); }
              catch (e) { notify(e.message); }
            }}
          />
        </div>
      ))}

      {list.data?.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--muted)", padding: "4px 0" }}>
          No signed agreements on file.
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <UploadBtn
          small
          label="Upload signed agreement"
          accept="image/*,.pdf"
          folder="customers"
          disabled={add.isPending}
          onUploaded={async ({ path, name }) => {
            try { await add.mutateAsync({ path, name }); notify("Agreement attached."); }
            catch (e) { notify(e.message); }
          }}
        />
      </div>
    </>
  );
}
